import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ATTRIBUTES, positionColor, positionAbbrev, formatDateDE } from "@/lib/playerConstants";
import { playerStats, REASON_OPTIONS, reasonLabel } from "@/lib/training";
import { playerMatchStats, noteColor, washStats } from "@/lib/matchStats";
import { Button } from "@/components/ui/button";
import StarRating from "@/components/StarRating";
import { Pencil, ArrowLeft, Trash2, CheckCircle2, AlertCircle, XCircle, Flame, Activity, Shirt } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";

export default function PlayerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, tRes, mRes] = await Promise.all([
          api.get(`/players/${id}`),
          api.get("/trainings"),
          api.get("/matches"),
        ]);
        setPlayer(pRes.data);
        setTrainings(tRes.data);
        setMatches(mRes.data);
      } catch {
        toast.error("Spieler nicht gefunden");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const tStats = useMemo(
    () => (player ? playerStats(player.id, trainings) : null),
    [player, trainings]
  );

  const mStats = useMemo(
    () => (player ? playerMatchStats(player.id, matches) : null),
    [player, matches]
  );

  const wStats = useMemo(
    () => (player ? washStats(player.id, matches) : null),
    [player, matches]
  );

  if (loading) return <div className="text-center py-20 text-zinc-500 font-display uppercase tracking-wider">Lädt…</div>;
  if (!player) return null;

  const radarData = ATTRIBUTES.map((a) => ({
    attribute: a.label,
    short: a.label.length > 9 ? a.label.slice(0, 7) + "." : a.label,
    value: player.attribute[a.key],
  }));

  const handleDelete = async () => {
    try {
      await api.delete(`/players/${player.id}`);
      toast.success("Spieler gelöscht");
      navigate("/dashboard");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        onClick={() => navigate("/dashboard")}
        className="text-xs uppercase tracking-wider text-zinc-500 hover:text-brand-lighter transition flex items-center gap-1 mb-6"
        data-testid="detail-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Zurück zum Dashboard
      </button>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className={`text-xs tracking-wide font-semibold ${positionColor(player.position)} mb-2`}>
            #{player.trikotnummer} · {positionAbbrev(player.position)}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-100" data-testid="detail-name">
            {player.name}
          </h1>
          <div className="text-zinc-400 text-sm mt-2">
            {formatDateDE(player.geburtsdatum)} · Jahrgang {player.jahrgang} · {player.koerpergroesse} cm · Trikot {player.trikotgroesse} · Hose {player.hosengroesse}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate(`/spieler/${player.id}/bearbeiten`)}
            className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11"
            data-testid="detail-edit-btn"
          >
            <Pencil className="h-4 w-4 mr-2" /> Bearbeiten
          </Button>
          <Button
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            className="bg-transparent border-zinc-700 text-zinc-100 hover:text-red-400 hover:bg-zinc-900 rounded-md h-11"
            data-testid="detail-delete-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Overall + Talent card */}
        <div className="lg:col-span-1 border border-zinc-800 bg-zinc-900/50 p-6 flex flex-col gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">Gesamtbewertung</div>
            <div className="font-display text-7xl font-bold text-brand-lighter tabular-nums leading-none" data-testid="detail-overall">
              {player.overall.toFixed(1)}
            </div>
            <div className="text-xs text-zinc-500 mt-2">Ø aller 12 Attribute</div>
          </div>
          <div className="border-t border-zinc-800 pt-5">
            <div className="text-[10px] uppercase tracking-wide text-zinc-400 mb-3">Talent</div>
            <StarRating value={player.talent} readOnly testIdPrefix="detail-talent" />
          </div>
        </div>

        {/* Radar */}
        <div className="lg:col-span-2 border border-zinc-800 bg-zinc-900/50 p-6">
          <div className="text-[10px] uppercase tracking-wide text-zinc-400 mb-4">Leistungsprofil</div>
          <div className="h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="75%">
                <PolarGrid stroke="#27272a" />
                <PolarAngleAxis
                  dataKey="short"
                  tick={{ fill: "#a1a1aa", fontSize: 11, fontFamily: "Inter" }}
                />
                <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: "#52525b", fontSize: 10 }} axisLine={false} />
                <Radar
                  name={player.name}
                  dataKey="value"
                  stroke="#3389c7"
                  fill="#3389c7"
                  fillOpacity={0.25}
                  strokeWidth={2}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Attribute list */}
        <div className="lg:col-span-3 border border-zinc-800 bg-zinc-900/50 p-6">
          <div className="text-[10px] uppercase tracking-wide text-zinc-400 mb-4">Alle Attribute</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {ATTRIBUTES.map((a) => {
              const v = player.attribute[a.key];
              return (
                <div key={a.key} data-testid={`detail-attr-${a.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-zinc-300">{a.label}</span>
                    <span className="font-display text-base font-bold text-brand-lighter tabular-nums">{v}</span>
                  </div>
                  <div className="h-1 bg-zinc-800 overflow-hidden">
                    <div className="h-full bg-brand-lighter" style={{ width: `${v * 10}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trainingsstatistik */}
        {tStats && (
          <div className="lg:col-span-3 border border-zinc-800 bg-zinc-900/50 p-6" data-testid="training-stats-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-brand-lighter" />
                <div className="text-[11px] uppercase tracking-wider text-zinc-400">Trainingsstatistik</div>
              </div>
              {tStats.total > 0 && (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wide text-zinc-500">Anwesenheitsquote</div>
                  <div className={
                    "font-display text-2xl font-bold tabular-nums " +
                    (tStats.quote >= 80 ? "text-emerald-400" : tStats.quote >= 50 ? "text-amber-400" : "text-rose-400")
                  } data-testid="ts-quote">
                    {tStats.quote.toFixed(1)} %
                  </div>
                </div>
              )}
            </div>

            {tStats.total === 0 ? (
              <div className="text-sm text-zinc-500 py-4 text-center">Noch keine Trainingsdaten für diesen Spieler.</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <TsStat label="Trainings" value={tStats.total} testId="ts-total" />
                  <TsStat label="Anwesend" value={tStats.counts.anwesend} color="text-emerald-400" icon={CheckCircle2} testId="ts-anw" />
                  <TsStat label="Entschuldigt" value={tStats.counts.entschuldigt} color="text-amber-400" icon={AlertCircle} testId="ts-ent" />
                  <TsStat label="Unentschuldigt" value={tStats.counts.unentschuldigt} color="text-rose-400" icon={XCircle} testId="ts-unent" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                  <ArtCard label="Mannschaftstraining" data={tStats.byArt.mannschaftstraining} testId="ts-mannschaft" />
                  <ArtCard label="Bonustraining" data={tStats.byArt.bonustraining} testId="ts-bonus" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Serien</div>
                      <Flame className="h-4 w-4 text-amber-400" />
                    </div>
                    <div className="flex justify-between items-end gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Aktuell</div>
                        <div className="font-display text-2xl font-bold text-brand-lighter tabular-nums" data-testid="ts-current-streak">{tStats.currentStreak}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Beste</div>
                        <div className="font-display text-2xl font-bold text-emerald-400 tabular-nums" data-testid="ts-best-streak">{tStats.bestStreak}</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Fehlgründe</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                      {REASON_OPTIONS.map((r) => (
                        <div key={r.key} className="flex items-center justify-between text-xs" data-testid={`ts-reason-${r.key}`}>
                          <span className="text-zinc-300">{r.label}</span>
                          <span className="font-display font-semibold text-amber-400 tabular-nums">{tStats.reasonCounts[r.key] || 0}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between text-xs col-span-2 mt-1 pt-1 border-t border-zinc-800" data-testid="ts-reason-unent">
                        <span className="text-zinc-300">Unentschuldigt</span>
                        <span className="font-display font-semibold text-rose-400 tabular-nums">{tStats.counts.unentschuldigt}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-tight">Spieler löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {player.name} wird unwiderruflich entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="detail-delete-confirm-btn">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Match-Bewertung Section */}
      {mStats && mStats.total > 0 && (
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6" data-testid="match-stats-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">Spieler-Note · Match-Bewertung</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Ø Saison</div>
              <div className={"font-display text-2xl font-bold tabular-nums " + noteColor(mStats.seasonAvg)} data-testid="ms-season">
                {mStats.seasonAvg == null ? "—" : mStats.seasonAvg.toFixed(1)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <MsStat label="Spiele" value={mStats.total} testId="ms-total" />
            <MsStat label="Ø Monat" value={mStats.monthAvg == null ? "—" : mStats.monthAvg.toFixed(1)} color={noteColor(mStats.monthAvg)} testId="ms-month" />
            <MsStat label="Ø Pflicht" value={mStats.pflichtAvg == null ? "—" : `${mStats.pflichtAvg.toFixed(1)}`} color={noteColor(mStats.pflichtAvg)} sub={`${mStats.pflichtCount} Sp.`} testId="ms-pflicht" />
            <MsStat label="Ø Test" value={mStats.testAvg == null ? "—" : `${mStats.testAvg.toFixed(1)}`} color={noteColor(mStats.testAvg)} sub={`${mStats.testCount} Sp.`} testId="ms-test" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <BestWorstCard label="Bestes Spiel" item={mStats.best} accent="text-emerald-400" />
            <BestWorstCard label="Schlechtestes Spiel" item={mStats.worst} accent="text-rose-400" />
          </div>

          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Formkurve (letzte {mStats.formCurve.length} Spiele)</div>
              <div className={
                "text-[10px] uppercase tracking-wide font-semibold " +
                (mStats.trend === "steigend" ? "text-emerald-400" : mStats.trend === "fallend" ? "text-rose-400" : "text-zinc-400")
              }>
                Trend: {mStats.trend}
              </div>
            </div>
            <div className="flex gap-1 sm:gap-1.5 items-end h-20">
              {mStats.formCurve.map((p, i) => {
                const height = ((7 - p.note) / 6) * 100; // invert: lower note = higher bar
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1 group" title={`${p.datum}: ${p.note.toFixed(1)}`}>
                    <div className="w-full flex items-end" style={{ height: "100%" }}>
                      <div
                        className={
                          "w-full rounded-t transition " +
                          (p.note <= 2.0 ? "bg-emerald-500" : p.note <= 3.0 ? "bg-lime-500" : p.note <= 4.0 ? "bg-amber-500" : p.note <= 5.0 ? "bg-orange-500" : "bg-rose-500")
                        }
                        style={{ height: `${Math.max(8, height)}%` }}
                      />
                    </div>
                    <div className="text-[9px] text-zinc-500 tabular-nums">{p.note.toFixed(1)}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-800 text-xs">
              <span className="text-zinc-500">Starter: <span className="text-emerald-400 font-semibold">{mStats.starterCount}</span></span>
              <span className="text-zinc-500">Ersatz: <span className="text-amber-400 font-semibold">{mStats.ersatzCount}</span></span>
            </div>
          </div>
        </div>
      )}

      {/* Wäschedienst Section */}
      {wStats && (
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6" data-testid="wash-stats-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shirt className="h-4 w-4 text-brand-lighter" />
              <div className="text-[11px] uppercase tracking-wider text-zinc-400">Wäschedienst</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Wäschen gesamt</div>
              <div className="font-display text-3xl font-bold text-brand-lighter tabular-nums" data-testid="wash-count">
                {wStats.count}
              </div>
            </div>
            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Letzte Wäsche</div>
              <div className="font-display text-xl font-semibold text-zinc-100 tabular-nums" data-testid="wash-last">
                {wStats.lastDate ? formatDateDE(wStats.lastDate) : "—"}
              </div>
            </div>
          </div>
          {wStats.count === 0 && (
            <div className="text-xs text-zinc-500 mt-3" data-testid="wash-empty">
              Dieser Spieler hat noch keine Wäsche übernommen.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TsStat({ label, value, color = "text-zinc-100", icon: Icon, testId }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 flex items-center gap-2.5">
      {Icon && <Icon className={"h-5 w-5 " + color} />}
      <div>
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className={"font-display text-xl font-bold tabular-nums " + color} data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}

function ArtCard({ label, data, testId }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-zinc-100">{label}</div>
        <div className={
          "font-display text-lg font-bold tabular-nums " +
          (data.quote >= 80 ? "text-emerald-400" : data.quote >= 50 ? "text-amber-400" : data.total === 0 ? "text-zinc-500" : "text-rose-400")
        }>
          {data.total === 0 ? "—" : `${data.quote.toFixed(1)} %`}
        </div>
      </div>
      <div className="text-xs text-zinc-400">
        {data.anwesend} von {data.total} Trainings
      </div>
    </div>
  );
}

function MsStat({ label, value, color = "text-zinc-100", sub, testId }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className={"font-display text-xl font-bold tabular-nums " + color} data-testid={testId}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function BestWorstCard({ label, item, accent }) {
  if (!item) return null;
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className={"font-display text-xl font-bold tabular-nums " + accent}>{item.rating.note.toFixed(1)}</div>
      </div>
      <div className="text-sm text-zinc-100 truncate">vs. {item.match.gegner}</div>
      <div className="text-[11px] text-zinc-500">{item.match.datum.slice(8,10)}.{item.match.datum.slice(5,7)}.{item.match.datum.slice(0,4)}</div>
    </div>
  );
}
